import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import AppleEmoji from '../components/Emoji';
import {
  IconImage,
  IconCalendar,
  IconSettings,
  IconBuilding,
  IconCheck,
  IconAlertCircle,
  IconCheckCircle,
  IconClock,
  IconClapperboard
} from '../components/Icons';

const DAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];


const BRAND_STYLES: Record<string, { color: string; bg: string; glow: string }> = {
  'espacio vital': {
    color: '#0060df',
    bg: 'rgba(219, 234, 254, 0.95)', // Azul Real / Blue
    glow: 'rgba(0, 112, 243, 0.18)'
  },
  'edwcar': {
    color: '#047857',
    bg: 'rgba(209, 250, 229, 0.95)', // Verde Esmeralda / Green (Diferencial)
    glow: 'rgba(16, 185, 129, 0.18)'
  },
  'futura': {
    color: '#c2410c',
    bg: 'rgba(255, 237, 213, 0.95)', // Naranja Mandarina
    glow: 'rgba(249, 115, 22, 0.18)'
  },
  'pentagono': {
    color: '#7c3aed',
    bg: 'rgba(243, 232, 255, 0.95)', // Púrpura / Violeta
    glow: 'rgba(124, 58, 237, 0.18)'
  },
  'baruk': {
    color: '#db2777',
    bg: 'rgba(253, 231, 243, 0.95)', // Rosa Frambuesa / Berry
    glow: 'rgba(219, 39, 119, 0.18)'
  },
  'genius': {
    color: '#b45309',
    bg: 'rgba(254, 243, 199, 0.95)', // Ámbar / Dorado Cálido
    glow: 'rgba(245, 158, 11, 0.18)'
  },
  'reditos': {
    color: '#dc2626',
    bg: 'rgba(254, 226, 226, 0.95)', // Rojo Coral
    glow: 'rgba(239, 68, 68, 0.18)'
  },
  'uan': {
    color: '#0f766e',
    bg: 'rgba(204, 251, 241, 0.95)', // Turquesa / Teal Océano
    glow: 'rgba(15, 118, 110, 0.18)'
  },
  'nexos': {
    color: '#65a30d',
    bg: 'rgba(236, 252, 203, 0.95)', // Verde Lima
    glow: 'rgba(132, 204, 22, 0.18)'
  },
  'zoik': {
    color: '#4338ca',
    bg: 'rgba(224, 231, 255, 0.95)', // Índigo Eléctrico
    glow: 'rgba(67, 56, 202, 0.18)'
  }
};

const DYNAMIC_PALETTES = [
  { color: '#0060df', bg: 'rgba(219, 234, 254, 0.9)', glow: 'rgba(0, 112, 243, 0.14)' },
  { color: '#047857', bg: 'rgba(209, 250, 229, 0.9)', glow: 'rgba(16, 185, 129, 0.14)' },
  { color: '#7c3aed', bg: 'rgba(243, 232, 255, 0.9)', glow: 'rgba(124, 58, 237, 0.14)' },
  { color: '#b45309', bg: 'rgba(254, 243, 199, 0.9)', glow: 'rgba(245, 158, 11, 0.14)' },
  { color: '#e11d48', bg: 'rgba(255, 228, 230, 0.9)', glow: 'rgba(225, 29, 72, 0.14)' },
  { color: '#0284c7', bg: 'rgba(224, 242, 254, 0.9)', glow: 'rgba(2, 132, 199, 0.14)' },
  { color: '#0f766e', bg: 'rgba(204, 251, 241, 0.9)', glow: 'rgba(15, 118, 110, 0.14)' },
  { color: '#c2410c', bg: 'rgba(255, 237, 213, 0.9)', glow: 'rgba(194, 65, 12, 0.14)' },
];

const getBrandStyle = (brandName?: string) => {
  if (!brandName) return DYNAMIC_PALETTES[0];
  const lower = brandName.toLowerCase();
  for (const [key, val] of Object.entries(BRAND_STYLES)) {
    if (lower.includes(key)) return val;
  }
  let hash = 0;
  for (let i = 0; i < brandName.length; i++) hash = brandName.charCodeAt(i) + ((hash << 5) - hash);
  const idx = Math.abs(hash) % DYNAMIC_PALETTES.length;
  return DYNAMIC_PALETTES[idx];
};

const STATUS_CONFIG: Record<string, { color: string; bg: string; border?: string; label: string; dot: string; glow: string }> = {
  DRAFT: {
    color: '#334155',
    bg: 'rgba(241, 245, 249, 0.9)',
    label: 'Borrador',
    dot: '#64748b',
    glow: 'rgba(100, 116, 139, 0.12)'
  },
  SCHEDULED: {
    color: '#0060df',
    bg: 'rgba(219, 234, 254, 0.9)',
    label: 'Programado',
    dot: '#0070f3',
    glow: 'rgba(0, 112, 243, 0.14)'
  },
  PUBLISHING: {
    color: '#b45309',
    bg: 'rgba(254, 243, 199, 0.9)',
    label: 'Procesando IG...',
    dot: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.14)'
  },
  PUBLISHED: {
    color: '#047857',
    bg: 'rgba(209, 250, 229, 0.9)',
    label: 'Publicado',
    dot: '#10b981',
    glow: 'rgba(16, 185, 129, 0.14)'
  },
  FAILED: {
    color: '#b91c1c',
    bg: 'rgba(254, 226, 226, 0.9)',
    label: 'Error',
    dot: '#ef4444',
    glow: 'rgba(239, 68, 68, 0.14)'
  }
};

const PLAT_LABEL: Record<string, string> = {
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  BOTH: 'FB + IG'
};

interface Post {
  id: string;
  caption: string;
  mediaUrl?: string;
  mediaType?: string;
  scheduledAt: string;
  status: string;
  platform: string;
  facebookPostId?: string;
  instagramPostId?: string;
  errorMessage?: string;
  brand: { id: string; name: string; logo?: string };
  createdBy: { firstName: string; lastName: string };
}

interface Brand {
  id: string;
  name: string;
  logo?: string;
  facebookPageId?: string;
  facebookPageToken?: string;
  instagramAccountId?: string;
}

const emptyForm = {
  caption: '',
  mediaUrl: '',
  mediaType: 'image',
  mediaUrls: [] as string[],
  pieceId: '',
  scheduledAt: '',
  platform: 'BOTH',
  brandId: ''
};

// Common Glass Styles
const glassCardStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.52) 100%)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  borderRadius: '22px',
  border: '1px solid rgba(255, 255, 255, 0.85)',
  boxShadow: '0 8px 30px 0 rgba(15, 42, 74, 0.04), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)'
};

const glassInputStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(15, 42, 74, 0.12)',
  borderRadius: '12px',
  color: '#0f2a4a',
  fontFamily: '"Montserrat", sans-serif',
  outline: 'none',
  transition: 'all 0.15s ease',
  boxShadow: 'none'
};

const glassModalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 42, 74, 0.38)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px'
};

const glassModalWindow: React.CSSProperties = {
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 255, 0.93) 100%)',
  backdropFilter: 'blur(28px) saturate(190%)',
  WebkitBackdropFilter: 'blur(28px) saturate(190%)',
  borderRadius: '26px',
  border: '1px solid rgba(255, 255, 255, 0.95)',
  boxShadow: '0 25px 60px rgba(15, 42, 74, 0.16), inset 0 1px 2px rgba(255, 255, 255, 0.95)',
  fontFamily: '"Montserrat", sans-serif'
};

const glassDropdownMenu: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(246, 250, 255, 0.95) 100%)',
  backdropFilter: 'blur(24px) saturate(190%)',
  WebkitBackdropFilter: 'blur(24px) saturate(190%)',
  borderRadius: '16px',
  border: '1px solid rgba(255, 255, 255, 0.95)',
  boxShadow: '0 16px 40px rgba(15, 42, 74, 0.16), 0 2px 6px rgba(0,0,0,0.04), inset 0 1px 2px rgba(255, 255, 255, 0.95)',
  zIndex: 1000,
  padding: '6px',
  maxHeight: '280px',
  overflowY: 'auto'
};

const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role !== 'CLIENTE';
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed
  const [posts, setPosts] = useState<Post[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [filterBrand, setFilterBrand] = useState('');
  const [loading, setLoading] = useState(false);
  const [rescheduleId, setRescheduleId] = useState<string | null>(null);
  const [newDate, setNewDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [metaForm, setMetaForm] = useState({ brandId: '' });
  const [oauthPages, setOauthPages] = useState<any[]>([]);
  const [selectedPage, setSelectedPage] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [approvedPieces, setApprovedPieces] = useState<any[]>([]);
  const [selectedPiece, setSelectedPieceForm] = useState<any>(null);

  // Estados de Glass Dropdowns
  const [openMetaDropdown, setOpenMetaDropdown] = useState(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(false);
  const [openFormBrandDropdown, setOpenFormBrandDropdown] = useState(false);
  const [openFormPlatformDropdown, setOpenFormPlatformDropdown] = useState(false);

  const metaDropdownRef = useRef<HTMLDivElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  const formBrandDropdownRef = useRef<HTMLDivElement>(null);
  const formPlatformDropdownRef = useRef<HTMLDivElement>(null);

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (metaDropdownRef.current && !metaDropdownRef.current.contains(e.target as Node)) {
        setOpenMetaDropdown(false);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target as Node)) {
        setOpenFilterDropdown(false);
      }
      if (formBrandDropdownRef.current && !formBrandDropdownRef.current.contains(e.target as Node)) {
        setOpenFormBrandDropdown(false);
      }
      if (formPlatformDropdownRef.current && !formPlatformDropdownRef.current.contains(e.target as Node)) {
        setOpenFormPlatformDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [pr, br] = await Promise.all([
        api.get('/calendar', { params: { year, month: month + 1, brandId: filterBrand || undefined } }),
        api.get('/brands'),
      ]);
      setPosts(Array.isArray(pr.data) ? pr.data : []);
      const brandsList = Array.isArray(br.data) ? br.data : (Array.isArray(br.data?.brands) ? br.data.brands : []);
      setBrands(brandsList);
    } catch {
      toast.error('Error al cargar el calendario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [year, month, filterBrand]);

  // Build calendar grid
  const dim = new Date(year, month + 1, 0).getDate();
  const fd = new Date(year, month, 1).getDay();
  const offset = fd === 0 ? 6 : fd - 1;
  const cells: (number | null)[] = [...Array(offset).fill(null)];
  for (let d = 1; d <= dim; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const postsForDay = (d: number) => {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return posts.filter(p => p.scheduledAt.startsWith(key));
  };

  const loadApprovedPieces = async (brandId: string) => {
    if (!brandId) {
      setApprovedPieces([]);
      return;
    }
    try {
      const r = await api.get('/pieces', { params: { brandId, status: 'APROBADO' } });
      const all = Array.isArray(r.data) ? r.data : [];
      setApprovedPieces(all.filter((p: any) => p.status === 'APROBADO'));
    } catch {
      setApprovedPieces([]);
    }
  };

  const openCreate = async (d?: number) => {
    const dateStr = d
      ? `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}T12:00`
      : '';
    const bId = filterBrand || '';
    setForm({ ...emptyForm, scheduledAt: dateStr, brandId: bId });
    setSelectedPieceForm(null);
    setSelectedDay(d || null);
    setSelectedPost(null);
    if (bId) await loadApprovedPieces(bId);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.caption || !form.scheduledAt || !form.brandId) {
      toast.error('El texto (caption), fecha y marca son requeridos');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/calendar', {
        ...form,
        scheduledAt: new Date(form.scheduledAt).toISOString(),
      });
      setPosts(prev => [...prev, r.data]);
      setShowModal(false);
      toast.success('Publicación programada correctamente');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Error al programar la publicación');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Estás seguro de eliminar esta publicación programada?')) return;
    try {
      await api.delete(`/calendar/${id}`);
      setPosts(prev => prev.filter(p => p.id !== id));
      setSelectedPost(null);
      toast.success('Publicación eliminada');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleId || !newDate) return;
    setRescheduling(true);
    try {
      await api.patch(`/calendar/${rescheduleId}/reschedule`, { scheduledAt: new Date(newDate).toISOString() });
      toast.success('Publicación reprogramada con éxito');
      setRescheduleId(null);
      setNewDate('');
      setSelectedPost(null);
      await load();
    } catch {
      toast.error('Error al reprogramar');
    } finally {
      setRescheduling(false);
    }
  };

  const handleSync = async (id: string) => {
    try {
      const r = await api.patch(`/calendar/${id}/sync`);
      setPosts(prev => prev.map(p => p.id === id ? r.data : p));
      setSelectedPost(r.data);
      toast.success('Estado sincronizado con Meta');
    } catch {
      toast.error('Error al sincronizar');
    }
  };

  const startMetaOAuth = (brandId: string) => {
    const url = `/api/calendar/meta/connect?brandId=${brandId}`;
    window.open(url, 'meta-auth', 'width=620,height=720,left=200,top=100');
    const handler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type !== 'META_OAUTH') return;
      window.removeEventListener('message', handler);
      if (e.data.error) {
        toast.error('Error Meta: ' + e.data.error);
        return;
      }
      if (e.data.key) {
        api.get(`/calendar/meta/session/${e.data.key}`).then(r => {
          setOauthPages(r.data.pages || []);
          setSelectedPage(null);
        }).catch(() => toast.error('Error al obtener páginas'));
      }
    };
    window.addEventListener('message', handler);
  };

  const handleSaveOAuthPage = async () => {
    if (!selectedPage) return;
    setSaving(true);
    try {
      await api.post('/calendar/meta/save', {
        brandId: metaForm.brandId,
        facebookPageId: selectedPage.id,
        facebookPageToken: selectedPage.access_token,
        instagramAccountId: selectedPage.instagramAccountId || null,
      });
      toast.success('Meta conectado correctamente');
      setShowMetaModal(false);
      setOauthPages([]);
      setSelectedPage(null);
      load();
    } catch {
      toast.error('Error al guardar credenciales Meta');
    } finally {
      setSaving(false);
    }
  };

  const openMetaConfig = (b: Brand) => {
    setMetaForm({ brandId: b.id });
    setOauthPages([]);
    setSelectedPage(null);
    setShowMetaModal(true);
  };

  const selectedBrandObj = brands.find(b => b.id === metaForm.brandId);
  const hasMeta = (brandId: string) => {
    const b = brands.find(x => x.id === brandId);
    return !!(b?.facebookPageId || b?.instagramAccountId);
  };

  const totalThisMonth = posts.length;
  const currentFilterBrandObj = brands.find(b => b.id === filterBrand);
  const currentFormBrandObj = brands.find(b => b.id === form.brandId);

  return (
    <div className="zoik-page-container" style={{
      display: 'flex',
      height: '100vh',
      maxHeight: '100vh',
      width: '100vw',
      maxWidth: '100vw',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #f1f5f9 0%, #e9eff6 50%, #edf3f9 100%)',
      fontFamily: '"Montserrat", sans-serif',
      position: 'relative'
    }}>
      <Sidebar />
      <main className="zoik-page-main" style={{
        flex: 1,
        height: '100vh',
        maxHeight: '100vh',
        padding: '28px 36px',
        overflowY: 'auto',
        overflowX: 'hidden', minWidth: 0 }}>
        
        {/* Top Header Card - Glassmorphism con overflow visible para permitir desplegables */}
        <div style={{
          ...glassCardStyle,
          padding: '20px 26px',
          marginBottom: '22px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px',
          overflow: 'visible',
          position: 'relative',
          zIndex: 40
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '38px',
                height: '38px',
                borderRadius: '12px',
                background: '#0070f3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'none',
                color: 'white'
              }}>
                <IconCalendar size={20} color="#ffffff" />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: '21px', fontWeight: 800, color: '#0f2a4a', letterSpacing: '-0.3px' }}>
                  Calendario de Publicaciones
                </h1>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgba(15, 42, 74, 0.52)', fontWeight: 500 }}>
                  Programa, gestiona y sincroniza tus entregas con Facebook e Instagram
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', position: 'relative' }}>
            
            {/* 1. GLASS DROPDOWN: CONFIGURAR META */}
            {isAdmin && (
              <div style={{ position: 'relative' }} ref={metaDropdownRef}>
                <button
                  onClick={() => {
                    setOpenMetaDropdown(!openMetaDropdown);
                    setOpenFilterDropdown(false);
                  }}
                  style={{
                    ...glassInputStyle,
                    padding: '9px 14px',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: openMetaDropdown ? '#0070f3' : '#0f2a4a',
                    border: openMetaDropdown ? '1px solid rgba(0, 112, 243, 0.4)' : '1px solid rgba(15, 42, 74, 0.12)',
                    background: openMetaDropdown ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)'
                  }}
                >
                  <IconSettings size={15} color={openMetaDropdown ? '#0070f3' : '#0f2a4a'} />
                  <span>Configurar Meta</span>
                  <span style={{
                    fontSize: '9px',
                    transition: 'transform 0.2s ease',
                    transform: openMetaDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    display: 'inline-block'
                  }}>
                    ▼
                  </span>
                </button>

                {openMetaDropdown && (
                  <div style={{ ...glassDropdownMenu, right: 0, width: '260px' }}>
                    <div style={{
                      padding: '8px 10px 6px',
                      fontSize: '10px',
                      fontWeight: 800,
                      color: 'rgba(15, 42, 74, 0.45)',
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase'
                    }}>
                      Selecciona una marca
                    </div>
                    {brands.length === 0 ? (
                      <div style={{ padding: '12px 10px', fontSize: '12px', color: 'rgba(15, 42, 74, 0.45)', textAlign: 'center' }}>
                        No hay marcas disponibles
                      </div>
                    ) : (
                      brands.map(b => {
                        const connected = hasMeta(b.id);
                        return (
                          <div
                            key={b.id}
                            onClick={() => {
                              openMetaConfig(b);
                              setOpenMetaDropdown(false);
                            }}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '8px',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => {
                              (e.currentTarget as HTMLDivElement).style.background = 'rgba(0, 112, 243, 0.08)';
                            }}
                            onMouseLeave={e => {
                              (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                              {b.logo ? (
                                <img src={b.logo} alt="" style={{ width: '22px', height: '22px', borderRadius: '6px', objectFit: 'cover' }} />
                              ) : (
                                <div style={{
                                  width: '22px',
                                  height: '22px',
                                  borderRadius: '6px',
                                  background: 'rgba(0, 112, 243, 0.1)',
                                  color: '#0070f3',
                                  fontSize: '11px',
                                  fontWeight: 800,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {b.name[0]}
                                </div>
                              )}
                              <span style={{ fontSize: '12px', fontWeight: 700, color: '#0f2a4a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {b.name}
                              </span>
                            </div>
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '6px',
                              background: connected ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                              color: connected ? '#059669' : '#d97706',
                              border: `1px solid ${connected ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                              flexShrink: 0
                            }}>
                              {connected ? '✓ Conectado' : '⚠ Sin Meta'}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 2. GLASS DROPDOWN: TODAS LAS MARCAS FILTER */}
            <div style={{ position: 'relative' }} ref={filterDropdownRef}>
              <button
                onClick={() => {
                  setOpenFilterDropdown(!openFilterDropdown);
                  setOpenMetaDropdown(false);
                }}
                style={{
                  ...glassInputStyle,
                  padding: '9px 14px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minWidth: '180px',
                  justifyContent: 'space-between',
                  color: openFilterDropdown ? '#0070f3' : '#0f2a4a',
                  border: openFilterDropdown ? '1px solid rgba(0, 112, 243, 0.4)' : '1px solid rgba(15, 42, 74, 0.12)',
                  background: openFilterDropdown ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.8)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                  <IconBuilding size={15} color={filterBrand ? '#0070f3' : '#0f2a4a'} />
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {currentFilterBrandObj ? currentFilterBrandObj.name : 'Todas las marcas'}
                  </span>
                </div>
                <span style={{
                  fontSize: '9px',
                  transition: 'transform 0.2s ease',
                  transform: openFilterDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  display: 'inline-block'
                }}>
                  ▼
                </span>
              </button>

              {openFilterDropdown && (
                <div style={{ ...glassDropdownMenu, right: 0, width: '240px' }}>
                  {/* Opción Todas las marcas */}
                  <div
                    onClick={() => {
                      setFilterBrand('');
                      setOpenFilterDropdown(false);
                    }}
                    style={{
                      padding: '8px 10px',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: !filterBrand ? 'rgba(0, 112, 243, 0.08)' : 'transparent',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => {
                      if (filterBrand) (e.currentTarget as HTMLDivElement).style.background = 'rgba(0, 112, 243, 0.05)';
                    }}
                    onMouseLeave={e => {
                      if (filterBrand) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconBuilding size={14} color={!filterBrand ? '#0070f3' : '#64748b'} />
                      <span style={{ fontSize: '12px', fontWeight: !filterBrand ? 800 : 600, color: !filterBrand ? '#0070f3' : '#0f2a4a' }}>
                        Todas las marcas
                      </span>
                    </div>
                    {!filterBrand && <IconCheck size={14} color="#0070f3" />}
                  </div>

                  {brands.map(b => {
                    const isSelected = filterBrand === b.id;
                    return (
                      <div
                        key={b.id}
                        onClick={() => {
                          setFilterBrand(b.id);
                          setOpenFilterDropdown(false);
                        }}
                        style={{
                          padding: '8px 10px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: isSelected ? 'rgba(0, 112, 243, 0.08)' : 'transparent',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={e => {
                          if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'rgba(0, 112, 243, 0.05)';
                        }}
                        onMouseLeave={e => {
                          if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                          {b.logo ? (
                            <img src={b.logo} alt="" style={{ width: '20px', height: '20px', borderRadius: '5px', objectFit: 'cover' }} />
                          ) : (
                            <div style={{
                              width: '20px',
                              height: '20px',
                              borderRadius: '5px',
                              background: 'rgba(0, 112, 243, 0.1)',
                              color: '#0070f3',
                              fontSize: '10px',
                              fontWeight: 800,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {b.name[0]}
                            </div>
                          )}
                          <span style={{ fontSize: '12px', fontWeight: isSelected ? 800 : 600, color: isSelected ? '#0070f3' : '#0f2a4a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {b.name}
                          </span>
                        </div>
                        {isSelected && <IconCheck size={14} color="#0070f3" />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {isAdmin && (
              <button
                onClick={() => openCreate()}
                style={{
                  padding: '9px 20px',
                  borderRadius: '12px',
                  border: 'none',
                  background: '#0070f3',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: 'none',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = '#0060df';
                  (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0, 112, 243, 0.3)';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = '#0070f3';
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                }}
                onMouseDown={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'scale(0.98)';
                }}
                onMouseUp={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                }}
              >
                <span style={{ fontSize: '15px', lineHeight: 1 }}>+</span> Programar
              </button>
            )}
          </div>
        </div>

        {/* Month Navigation Card - Glassmorphism */}
        <div style={{
          ...glassCardStyle,
          padding: '12px 20px',
          marginBottom: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => {
                if (month === 0) {
                  setMonth(11);
                  setYear(y => y - 1);
                } else setMonth(m => m - 1);
              }}
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '10px',
                border: '1px solid rgba(15, 42, 74, 0.1)',
                background: 'rgba(255, 255, 255, 0.75)',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#0f2a4a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'none',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'white';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.75)';
              }}
            >
              ‹
            </button>

            <div style={{
              fontSize: '17px',
              fontWeight: 800,
              color: '#0f2a4a',
              minWidth: '200px',
              textAlign: 'center',
              letterSpacing: '-0.2px'
            }}>
              {MONTHS[month]} <span style={{ color: '#0070f3' }}>{year}</span>
            </div>

            <button
              onClick={() => {
                if (month === 11) {
                  setMonth(0);
                  setYear(y => y + 1);
                } else setMonth(m => m + 1);
              }}
              style={{
                width: '34px',
                height: '34px',
                borderRadius: '10px',
                border: '1px solid rgba(15, 42, 74, 0.1)',
                background: 'rgba(255, 255, 255, 0.75)',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#0f2a4a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'none',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'white';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.75)';
              }}
            >
              ›
            </button>

            <button
              onClick={() => {
                setYear(now.getFullYear());
                setMonth(now.getMonth());
              }}
              style={{
                marginLeft: '8px',
                padding: '6px 14px',
                borderRadius: '10px',
                border: '1px solid rgba(0, 112, 243, 0.25)',
                background: 'rgba(0, 112, 243, 0.08)',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                color: '#0070f3',
                fontFamily: '"Montserrat", sans-serif',
                boxShadow: 'none',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(0, 112, 243, 0.15)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(0, 112, 243, 0.08)';
              }}
              onMouseDown={e => {
                (e.currentTarget as HTMLElement).style.transform = 'scale(0.97)';
              }}
              onMouseUp={e => {
                (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
              }}
            >
              Hoy
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              padding: '6px 12px',
              borderRadius: '10px',
              background: 'rgba(15, 42, 74, 0.04)',
              border: '1px solid rgba(15, 42, 74, 0.06)',
              fontSize: '11px',
              fontWeight: 600,
              color: 'rgba(15, 42, 74, 0.65)',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center'
            }}>
              <AppleEmoji symbol="📅" size={15} style={{ marginRight: '6px' }} />
              <span><strong style={{ color: '#0f2a4a' }}>{totalThisMonth}</strong> publicaciones este mes</span>
            </div>

            {loading && (
              <span style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#0070f3',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#0070f3',
                  display: 'inline-block'
                }}></span>
                Actualizando...
              </span>
            )}
          </div>
        </div>

        {/* Main Calendar Grid Card - Glassmorphism */}
        <div style={{
          ...glassCardStyle,
          overflow: 'hidden',
          padding: '0',
          boxShadow: '0 10px 32px 0 rgba(15, 42, 74, 0.05), inset 0 1px 2px 0 rgba(255, 255, 255, 0.95)',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Day Headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(240, 245, 252, 0.4) 100%)',
            borderBottom: '1px solid rgba(15, 42, 74, 0.06)',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {DAYS.map(d => (
              <div key={d} style={{
                padding: '12px 6px',
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: 800,
                color: '#0f2a4a',
                letterSpacing: '0.8px',
                textTransform: 'uppercase',
                opacity: 0.75,
                minWidth: 0,
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}>
                {d}
              </div>
            ))}
          </div>

          {/* Grid Cells */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {cells.map((d, i) => {
              if (!d) {
                return (
                  <div
                    key={'empty-' + i}
                    style={{
                      minHeight: '142px',
                      background: 'rgba(241, 245, 249, 0.25)',
                      borderRight: '1px solid rgba(15, 42, 74, 0.05)',
                      borderBottom: '1px solid rgba(15, 42, 74, 0.05)',
                      minWidth: 0,
                      boxSizing: 'border-box'
                    }}
                  />
                );
              }

              const todayD = now.getFullYear() === year && now.getMonth() === month && now.getDate() === d;
              const dayPosts = postsForDay(d);

              return (
                <div
                  key={d}
                  onClick={() => isAdmin && openCreate(d)}
                  style={{
                    minHeight: '142px',
                    borderRight: '1px solid rgba(15, 42, 74, 0.05)',
                    borderBottom: '1px solid rgba(15, 42, 74, 0.05)',
                    padding: '8px 6px',
                    cursor: isAdmin ? 'pointer' : 'default',
                    transition: 'all 0.15s ease',
                    background: todayD ? 'rgba(0, 112, 243, 0.03)' : 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    minWidth: 0,
                    overflow: 'hidden',
                    boxSizing: 'border-box'
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLDivElement).style.background = todayD
                      ? 'rgba(0, 112, 243, 0.06)'
                      : 'rgba(255, 255, 255, 0.8)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLDivElement).style.background = todayD
                      ? 'rgba(0, 112, 243, 0.03)'
                      : 'transparent';
                  }}
                >
                  {/* Day number header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}>
                    <div style={{
                      width: '26px',
                      height: '26px',
                      borderRadius: '8px',
                      background: todayD ? '#0070f3' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'none'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: todayD ? 800 : 700,
                        color: todayD ? '#ffffff' : 'rgba(15, 42, 74, 0.75)'
                      }}>
                        {d}
                      </span>
                    </div>

                    {dayPosts.length > 0 && (
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: '#0070f3',
                        background: 'rgba(0, 112, 243, 0.08)',
                        padding: '1px 6px',
                        borderRadius: '6px',
                        boxShadow: 'none'
                      }}>
                        {dayPosts.length}
                      </span>
                    )}
                  </div>

                  {/* Liquid Glass Post Chips (Espacioso con Aire y Ajuste Perfecto sin Desbordarse) */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, padding: '2px 0', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
                    {dayPosts.slice(0, 3).map(p => {
                      const bStyle = getBrandStyle(p.brand?.name);
                      const postDate = new Date(p.scheduledAt);
                      const timeStr = !isNaN(postDate.getTime())
                        ? `${String(postDate.getHours()).padStart(2, '0')}:${String(postDate.getMinutes()).padStart(2, '0')}`
                        : '';
                      
                      const isVideo = 
                        p.mediaType === 'video' || 
                        (p.mediaUrl && /\.(mp4|mov|webm|m4v|avi|mkv)/i.test(p.mediaUrl)) ||
                        (p.caption && /\b(reel|reels|video|tiktok|historias|story)\b/i.test(p.caption));

                      return (
                        <div
                          key={p.id}
                          onClick={e => {
                            e.stopPropagation();
                            setSelectedPost(p);
                          }}
                          style={{
                            padding: '8px 9px',
                            borderRadius: '11px',
                            background: bStyle.bg,
                            backdropFilter: 'blur(16px) saturate(180%)',
                            WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                            border: 'none',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            transition: 'all 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
                            boxShadow: `0 3px 10px 0 ${bStyle.glow}, inset 0 1.5px 2px 0 rgba(255, 255, 255, 0.95)`,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px',
                            userSelect: 'none',
                            width: '100%',
                            minWidth: 0,
                            boxSizing: 'border-box'
                          }}
                          onMouseEnter={e => {
                            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                            (e.currentTarget as HTMLDivElement).style.boxShadow = `0 8px 18px -2px ${bStyle.glow}, inset 0 2px 3px 0 rgba(255, 255, 255, 1)`;
                          }}
                          onMouseLeave={e => {
                            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                            (e.currentTarget as HTMLDivElement).style.boxShadow = `0 3px 10px 0 ${bStyle.glow}, inset 0 1.5px 2px 0 rgba(255, 255, 255, 0.95)`;
                          }}
                          onMouseDown={e => {
                            (e.currentTarget as HTMLDivElement).style.transform = 'scale(0.98)';
                          }}
                          onMouseUp={e => {
                            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                          }}
                        >
                          {/* LADO IZQUIERDO: Solo textos con truncation segura */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1, overflow: 'hidden' }}>
                            <div style={{
                              fontSize: '11px',
                              fontWeight: 800,
                              color: bStyle.color,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              letterSpacing: '-0.2px',
                              lineHeight: 1.25
                            }}>
                              {p.brand?.name || 'Marca'}
                            </div>
                            <div style={{
                              fontSize: '9.5px',
                              fontWeight: 500,
                              color: 'rgba(15, 42, 74, 0.74)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: 1.25
                            }}>
                              {p.caption ? p.caption.slice(0, 32) : 'Sin descripción'}
                            </div>
                          </div>

                          {/* LADO DERECHO: Reloj con hora + Formato */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                            {timeStr && (
                              <div
                                title={`Hora: ${timeStr}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2.5px',
                                  fontSize: '9px',
                                  fontWeight: 700,
                                  color: 'rgba(15, 42, 74, 0.72)',
                                  background: 'rgba(255, 255, 255, 0.88)',
                                  backdropFilter: 'blur(6px)',
                                  padding: '2px 4.5px',
                                  borderRadius: '5px',
                                  boxShadow: '0 1px 2px rgba(15, 42, 74, 0.04), inset 0 1px 1px #ffffff',
                                  lineHeight: 1,
                                  flexShrink: 0
                                }}
                              >
                                <IconClock size={10} color="rgba(15, 42, 74, 0.55)" />
                                <span>{timeStr}</span>
                              </div>
                            )}

                            {/* Formato: Claqueta (video/reel) o Imagen (post/foto) */}
                            <div
                              title={isVideo ? 'Video / Reel' : 'Post / Imagen'}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '20px',
                                height: '20px',
                                borderRadius: '6px',
                                background: 'rgba(255, 255, 255, 0.88)',
                                backdropFilter: 'blur(6px)',
                                color: bStyle.color,
                                boxShadow: '0 1px 2px rgba(15, 42, 74, 0.04), inset 0 1px 1px #ffffff',
                                flexShrink: 0
                              }}
                            >
                              {isVideo ? (
                                <IconClapperboard size={12} color="currentColor" />
                              ) : (
                                <IconImage size={12} color="currentColor" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {dayPosts.length > 3 && (
                      <div
                        style={{
                          fontSize: '9.5px',
                          fontWeight: 700,
                          color: '#0070f3',
                          padding: '3.5px 8px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(235, 245, 255, 0.75) 100%)',
                          backdropFilter: 'blur(10px)',
                          WebkitBackdropFilter: 'blur(10px)',
                          border: 'none',
                          textAlign: 'center',
                          boxShadow: '0 2px 8px rgba(0, 112, 243, 0.08), inset 0 1px 1.5px rgba(255, 255, 255, 0.95)',
                          cursor: 'pointer',
                          transition: 'all 0.18s ease'
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLDivElement).style.background = 'rgba(255, 255, 255, 0.98)';
                          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
                          (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 12px rgba(0, 112, 243, 0.15), inset 0 1px 1.5px #ffffff';
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLDivElement).style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(235, 245, 255, 0.75) 100%)';
                          (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
                          (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0, 112, 243, 0.08), inset 0 1px 1.5px rgba(255, 255, 255, 0.95)';
                        }}
                      >
                        +{dayPosts.length - 3} más
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend / Status Badges - Liquid Glass (Sin Trazo) */}
        <div style={{
          ...glassCardStyle,
          padding: '12px 20px',
          marginTop: '18px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', letterSpacing: '0.4px', textTransform: 'uppercase', marginRight: '4px' }}>
            Estados:
          </span>
          {Object.entries(STATUS_CONFIG).map(([k, cfg]) => (
            <div key={k} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 12px',
              borderRadius: '10px',
              background: cfg.bg,
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: 'none',
              boxShadow: `0 3px 10px 0 ${cfg.glow}, inset 0 1.5px 2px 0 rgba(255, 255, 255, 0.9)`
            }}>
              <div style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                background: cfg.dot,
                boxShadow: `0 0 5px ${cfg.dot}`
              }} />
              <span style={{ fontSize: '11px', fontWeight: 700, color: cfg.color }}>
                {cfg.label}
              </span>
            </div>
          ))}
        </div>
      </main>

      {/* MODAL: Ver Detalle de Publicación */}
      {selectedPost && (
        <div style={glassModalOverlay} onClick={() => setSelectedPost(null)}>
          <div
            style={{
              ...glassModalWindow,
              padding: '28px 32px',
              width: '460px',
              maxWidth: '92vw',
              maxHeight: '85vh',
              overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div style={{ fontSize: '17px', fontWeight: 800, color: '#0f2a4a', letterSpacing: '-0.2px' }}>
                Detalle de Publicación
              </div>
              <button
                onClick={() => setSelectedPost(null)}
                style={{
                  background: 'rgba(15, 42, 74, 0.06)',
                  border: 'none',
                  width: '28px',
                  height: '28px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#0f2a4a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.15s ease'
                }}
              >
                ✕
              </button>
            </div>

            {/* Brand & Status Banner */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              padding: '12px',
              background: 'rgba(255, 255, 255, 0.65)',
              borderRadius: '16px',
              border: '1px solid rgba(255, 255, 255, 0.9)',
              boxShadow: 'none'
            }}>
              {selectedPost.brand.logo ? (
                <img
                  src={selectedPost.brand.logo}
                  alt={selectedPost.brand.name}
                  style={{ width: '38px', height: '38px', borderRadius: '10px', objectFit: 'cover', border: '1px solid rgba(15, 42, 74, 0.08)' }}
                />
              ) : (
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: '#0070f3',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 800,
                  color: 'white',
                  boxShadow: 'none'
                }}>
                  {selectedPost.brand.name[0]}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '14px', color: '#0f2a4a' }}>
                  {selectedPost.brand.name}
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(15, 42, 74, 0.5)', fontWeight: 500 }}>
                  {new Date(selectedPost.scheduledAt).toLocaleString('es-CO', {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              {(() => {
                const cfg = STATUS_CONFIG[selectedPost.status] || STATUS_CONFIG.DRAFT;
                return (
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '10px',
                    background: cfg.bg,
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: 'none',
                    boxShadow: `0 3px 10px 0 ${cfg.glow}, inset 0 1.5px 2px 0 rgba(255, 255, 255, 0.95)`,
                    color: cfg.color,
                    fontSize: '11px',
                    fontWeight: 700,
                    flexShrink: 0
                  }}>
                    {cfg.label}
                  </span>
                );
              })()}
            </div>

            {/* Media Preview */}
            {selectedPost.mediaUrl && (
              <div style={{ marginBottom: '16px', borderRadius: '14px', overflow: 'hidden', border: '1px solid rgba(15, 42, 74, 0.08)' }}>
                {selectedPost.mediaType === 'video' || /\.(mp4|mov|webm)/i.test(selectedPost.mediaUrl) ? (
                  <video
                    src={selectedPost.mediaUrl}
                    controls
                    style={{ width: '100%', maxHeight: '280px', display: 'block', background: '#0f172a' }}
                  />
                ) : (
                  <img
                    src={selectedPost.mediaUrl}
                    alt="Media preview"
                    style={{ width: '100%', maxHeight: '280px', objectFit: 'contain', background: 'rgba(255, 255, 255, 0.5)', display: 'block' }}
                  />
                )}
              </div>
            )}

            {/* Caption Card */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.6)',
              border: '1px solid rgba(15, 42, 74, 0.08)',
              borderRadius: '14px',
              padding: '14px 16px',
              marginBottom: '14px',
              boxShadow: 'none'
            }}>
              <div style={{ fontSize: '10px', fontWeight: 800, color: 'rgba(15, 42, 74, 0.45)', marginBottom: '6px', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
                Caption
              </div>
              <div style={{ fontSize: '13px', color: '#0f2a4a', lineHeight: '1.6', whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                {selectedPost.caption || <span style={{ color: 'rgba(15, 42, 74, 0.3)' }}>Sin descripción</span>}
              </div>
            </div>

            {/* Info Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
              <div style={{ background: 'rgba(255, 255, 255, 0.6)', border: '1px solid rgba(15, 42, 74, 0.06)', borderRadius: '12px', padding: '10px 12px', boxShadow: 'none' }}>
                <div style={{ fontSize: '10px', color: 'rgba(15, 42, 74, 0.45)', fontWeight: 700, marginBottom: '3px' }}>PLATAFORMA</div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>{PLAT_LABEL[selectedPost.platform] || selectedPost.platform}</div>
              </div>
              <div style={{ background: 'rgba(255, 255, 255, 0.6)', border: '1px solid rgba(15, 42, 74, 0.06)', borderRadius: '12px', padding: '10px 12px', boxShadow: 'none' }}>
                <div style={{ fontSize: '10px', color: 'rgba(15, 42, 74, 0.45)', fontWeight: 700, marginBottom: '3px' }}>PUBLICADO POR</div>
                <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f2a4a' }}>{selectedPost.createdBy?.firstName} {selectedPost.createdBy?.lastName}</div>
              </div>
            </div>

            {/* Social Links */}
            {(selectedPost.facebookPostId || selectedPost.instagramPostId) && (
              <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                {selectedPost.facebookPostId && (
                  <a
                    href={`https://www.facebook.com/${selectedPost.facebookPostId}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      flex: 1,
                      padding: '9px',
                      borderRadius: '10px',
                      background: 'rgba(24, 119, 242, 0.1)',
                      border: '1px solid rgba(24, 119, 242, 0.25)',
                      color: '#1877f2',
                      fontSize: '12px',
                      fontWeight: 700,
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'block',
                      boxShadow: 'none',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(24, 119, 242, 0.18)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(24, 119, 242, 0.1)';
                    }}
                  >
                    Ver en Facebook →
                  </a>
                )}
                {selectedPost.instagramPostId && (
                  <a
                    href={`https://www.instagram.com/p/${selectedPost.instagramPostId}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      flex: 1,
                      padding: '9px',
                      borderRadius: '10px',
                      background: 'rgba(225, 48, 108, 0.1)',
                      border: '1px solid rgba(225, 48, 108, 0.25)',
                      color: '#e1306c',
                      fontSize: '12px',
                      fontWeight: 700,
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'block',
                      boxShadow: 'none',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(225, 48, 108, 0.18)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = 'rgba(225, 48, 108, 0.1)';
                    }}
                  >
                    Ver en Instagram →
                  </a>
                )}
              </div>
            )}

            {/* Error Message */}
            {selectedPost.errorMessage && (
              <div style={{
                background: 'rgba(254, 242, 242, 0.85)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '14px',
                fontSize: '12px',
                color: '#dc2626',
                fontWeight: 600,
                boxShadow: 'none'
              }}>
                ⚠ {selectedPost.errorMessage}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => handleSync(selectedPost.id)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '10px',
                  border: '1px solid rgba(15, 42, 74, 0.12)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  color: '#0f2a4a',
                  boxShadow: 'none',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.background = 'white';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.8)';
                }}
              >
                ↻ Sincronizar
              </button>

              {(selectedPost.status === 'SCHEDULED' || selectedPost.status === 'FAILED' || selectedPost.status === 'DRAFT') && (
                <button
                  onClick={() => {
                    const d = new Date(selectedPost.scheduledAt);
                    const pad = (n: number) => String(n).padStart(2, '0');
                    setNewDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                    setRescheduleId(selectedPost.id);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 112, 243, 0.3)',
                    background: 'rgba(0, 112, 243, 0.08)',
                    color: '#0070f3',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0, 112, 243, 0.15)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(0, 112, 243, 0.08)';
                  }}
                  onMouseDown={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'scale(0.98)';
                  }}
                  onMouseUp={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                  }}
                >
                  <IconCalendar size={14} color="#0070f3" /> Reprogramar
                </button>
              )}

              {isAdmin && (
                <button
                  onClick={() => handleDelete(selectedPost.id)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '10px',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    background: 'rgba(239, 68, 68, 0.08)',
                    color: '#dc2626',
                    fontSize: '12px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(239, 68, 68, 0.15)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'rgba(239, 68, 68, 0.08)';
                  }}
                  onMouseDown={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'scale(0.98)';
                  }}
                  onMouseUp={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                  }}
                >
                  Eliminar
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Reprogramar Fecha/Hora */}
      {rescheduleId && (
        <div style={glassModalOverlay} onClick={() => setRescheduleId(null)}>
          <div
            style={{
              ...glassModalWindow,
              padding: '28px',
              width: '360px',
              maxWidth: '92vw'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: '17px', fontWeight: 800, color: '#0f2a4a', marginBottom: '14px', letterSpacing: '-0.2px' }}>
              Reprogramar Publicación
            </div>
            <div style={{ fontSize: '11px', color: 'rgba(15, 42, 74, 0.55)', marginBottom: '8px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
              Nueva Fecha y Hora
            </div>
            <input
              type="datetime-local"
              value={newDate}
              onChange={e => setNewDate(e.target.value)}
              style={{
                ...glassInputStyle,
                width: '100%',
                padding: '11px 12px',
                fontSize: '13px',
                boxSizing: 'border-box',
                marginBottom: '18px'
              }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setRescheduleId(null)}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '12px',
                  border: '1px solid rgba(15, 42, 74, 0.12)',
                  background: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  color: '#0f2a4a',
                  boxShadow: 'none'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleReschedule}
                disabled={!newDate || rescheduling}
                style={{
                  flex: 1,
                  padding: '10px',
                  borderRadius: '12px',
                  border: 'none',
                  background: '#0070f3',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: '"Montserrat", sans-serif',
                  opacity: (!newDate || rescheduling) ? 0.6 : 1,
                  boxShadow: 'none',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={e => {
                  if (newDate && !rescheduling) {
                    (e.currentTarget as HTMLElement).style.background = '#0060df';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0, 112, 243, 0.3)';
                  }
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.background = '#0070f3';
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                }}
                onMouseDown={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'scale(0.98)';
                }}
                onMouseUp={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                }}
              >
                {rescheduling ? 'Guardando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Crear / Programar Publicación */}
      {showModal && (
        <div style={glassModalOverlay} onClick={() => setShowModal(false)}>
          <div
            style={{
              ...glassModalWindow,
              padding: '28px 32px',
              width: '500px',
              maxWidth: '92vw',
              maxHeight: '88vh',
              overflowY: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f2a4a', letterSpacing: '-0.2px' }}>
                  Programar Publicación
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(15, 42, 74, 0.45)', fontWeight: 500 }}>
                  Completa los campos para agendar contenido en tus redes
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'rgba(15, 42, 74, 0.06)',
                  border: 'none',
                  width: '28px',
                  height: '28px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#0f2a4a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              
              {/* 3. GLASS DROPDOWN DENTRO DEL FORMULARIO: SELECCIONAR MARCA */}
              <div style={{ position: 'relative' }} ref={formBrandDropdownRef}>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                  Marca *
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setOpenFormBrandDropdown(!openFormBrandDropdown);
                    setOpenFormPlatformDropdown(false);
                  }}
                  style={{
                    ...glassInputStyle,
                    width: '100%',
                    padding: '10px 14px',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxSizing: 'border-box',
                    border: openFormBrandDropdown ? '1px solid rgba(0, 112, 243, 0.4)' : '1px solid rgba(15, 42, 74, 0.12)',
                    background: openFormBrandDropdown ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    {currentFormBrandObj ? (
                      <>
                        {currentFormBrandObj.logo ? (
                          <img src={currentFormBrandObj.logo} alt="" style={{ width: '20px', height: '20px', borderRadius: '5px', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '20px', height: '20px', borderRadius: '5px', background: '#0070f3', color: 'white', fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {currentFormBrandObj.name[0]}
                          </div>
                        )}
                        <span style={{ color: '#0f2a4a' }}>{currentFormBrandObj.name}</span>
                        <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '6px', background: hasMeta(currentFormBrandObj.id) ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)', color: hasMeta(currentFormBrandObj.id) ? '#059669' : '#d97706' }}>
                          {hasMeta(currentFormBrandObj.id) ? '✓ Meta' : '⚠ Sin Meta'}
                        </span>
                      </>
                    ) : (
                      <span style={{ color: 'rgba(15, 42, 74, 0.45)', fontWeight: 500 }}>Selecciona una marca...</span>
                    )}
                  </div>
                  <span style={{
                    fontSize: '9px',
                    transition: 'transform 0.2s ease',
                    transform: openFormBrandDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    display: 'inline-block'
                  }}>
                    ▼
                  </span>
                </button>

                {openFormBrandDropdown && (
                  <div style={{ ...glassDropdownMenu, left: 0, right: 0, width: '100%', boxSizing: 'border-box' }}>
                    {brands.length === 0 ? (
                      <div style={{ padding: '12px 10px', fontSize: '12px', color: 'rgba(15, 42, 74, 0.45)', textAlign: 'center' }}>
                        No hay marcas disponibles
                      </div>
                    ) : (
                      brands.map(b => {
                        const isSelected = form.brandId === b.id;
                        const connected = hasMeta(b.id);
                        return (
                          <div
                            key={b.id}
                            onClick={() => {
                              setForm(f => ({ ...f, brandId: b.id, mediaUrl: '', caption: '' }));
                              setSelectedPieceForm(null);
                              loadApprovedPieces(b.id);
                              setOpenFormBrandDropdown(false);
                            }}
                            style={{
                              padding: '10px 12px',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              background: isSelected ? 'rgba(0, 112, 243, 0.08)' : 'transparent',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => {
                              if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'rgba(0, 112, 243, 0.05)';
                            }}
                            onMouseLeave={e => {
                              if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                              {b.logo ? (
                                <img src={b.logo} alt="" style={{ width: '22px', height: '22px', borderRadius: '6px', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: '22px', height: '22px', borderRadius: '6px', background: 'rgba(0, 112, 243, 0.1)', color: '#0070f3', fontSize: '11px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {b.name[0]}
                                </div>
                              )}
                              <span style={{ fontSize: '13px', fontWeight: isSelected ? 800 : 600, color: isSelected ? '#0070f3' : '#0f2a4a' }}>
                                {b.name}
                              </span>
                            </div>
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '6px',
                              background: connected ? 'rgba(16, 185, 129, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                              color: connected ? '#059669' : '#d97706'
                            }}>
                              {connected ? '✓ Meta' : '⚠ Sin Meta'}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Caption */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                  Caption / Texto *
                </label>
                <textarea
                  value={form.caption}
                  onChange={e => setForm(f => ({ ...f, caption: e.target.value }))}
                  rows={4}
                  placeholder="Escribe el texto de la publicación, hashtags y copy..."
                  style={{
                    ...glassInputStyle,
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Piezas aprobadas */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                  Pieza Aprobada {form.brandId && <span style={{ fontWeight: 500, color: 'rgba(15, 42, 74, 0.45)' }}>({approvedPieces.length} disponibles)</span>}
                </label>
                {!form.brandId ? (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.5)',
                    border: '1px dashed rgba(15, 42, 74, 0.15)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: 'rgba(15, 42, 74, 0.45)'
                  }}>
                    Selecciona una marca para ver sus piezas aprobadas
                  </div>
                ) : approvedPieces.length === 0 ? (
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.5)',
                    border: '1px dashed rgba(15, 42, 74, 0.15)',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: 'rgba(15, 42, 74, 0.45)'
                  }}>
                    No hay piezas aprobadas disponibles para esta marca
                  </div>
                ) : (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '8px',
                    maxHeight: '180px',
                    overflowY: 'auto',
                    padding: '4px'
                  }}>
                    {approvedPieces.map((p: any) => {
                      const thumb = p.fileUrl || (Array.isArray(p.files) && p.files[0]?.url) || null;
                      const isSelected = selectedPiece?.id === p.id;
                      return (
                        <div
                          key={p.id}
                          onClick={() => {
                            setSelectedPieceForm(p);
                            const isCarousel = p.type === 'CARRUSEL';
                            const allUrls = isCarousel && Array.isArray(p.files) ? p.files.map((f: any) => f.url || f).filter(Boolean) : [];
                            setForm(f => ({
                              ...f,
                              mediaUrl: thumb || '',
                              mediaType: isCarousel ? 'carousel' : (thumb && /\.(mp4|mov|webm)/i.test(thumb) ? 'video' : 'image'),
                              mediaUrls: allUrls,
                              pieceId: p.id,
                              caption: f.caption || p.title
                            }));
                          }}
                          style={{
                            borderRadius: '12px',
                            border: `2px solid ${isSelected ? '#0070f3' : 'rgba(15, 42, 74, 0.08)'}`,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            background: isSelected ? 'rgba(0, 112, 243, 0.08)' : 'rgba(255, 255, 255, 0.7)',
                            position: 'relative',
                            transition: 'all 0.15s ease',
                            boxShadow: 'none'
                          }}
                        >
                          {thumb ? (
                            <img src={thumb} alt={p.title} style={{ width: '100%', height: '70px', objectFit: 'cover', display: 'block' }} />
                          ) : (
                            <div style={{ width: '100%', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15, 42, 74, 0.04)' }}>
                              <IconImage size={24} color="rgba(15, 42, 74, 0.4)" />
                            </div>
                          )}
                          <div style={{
                            padding: '4px 6px',
                            fontSize: '9.5px',
                            fontWeight: 700,
                            color: isSelected ? '#0070f3' : '#0f2a4a',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {p.title}
                          </div>
                          {isSelected && (
                            <div style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              background: '#0070f3',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: 'none'
                            }}>
                              <IconCheck size={11} color="white" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {selectedPiece && form.mediaUrl && (
                  <div style={{
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    background: 'rgba(0, 112, 243, 0.08)',
                    border: '1px solid rgba(0, 112, 243, 0.2)',
                    borderRadius: '10px',
                    boxShadow: 'none'
                  }}>
                    <img src={form.mediaUrl} alt="Selected" style={{ width: '30px', height: '30px', borderRadius: '6px', objectFit: 'cover' }} />
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#0070f3', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {selectedPiece.title}
                    </span>
                    <button
                      onClick={() => {
                        setSelectedPieceForm(null);
                        setForm(f => ({ ...f, mediaUrl: '', mediaUrls: [], pieceId: '', mediaType: 'image', caption: '' }));
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'rgba(15, 42, 74, 0.4)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '2px 4px'
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              {/* Date & Platform with Glass Dropdown */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                    Fecha y Hora *
                  </label>
                  <input
                    type="datetime-local"
                    value={form.scheduledAt}
                    onChange={e => setForm(f => ({ ...f, scheduledAt: e.target.value }))}
                    style={{
                      ...glassInputStyle,
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '12px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {/* 4. GLASS DROPDOWN: PLATAFORMA */}
                <div style={{ position: 'relative' }} ref={formPlatformDropdownRef}>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#0f2a4a', display: 'block', marginBottom: '6px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
                    Plataforma
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenFormPlatformDropdown(!openFormPlatformDropdown);
                      setOpenFormBrandDropdown(false);
                    }}
                    style={{
                      ...glassInputStyle,
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '12px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      boxSizing: 'border-box',
                      border: openFormPlatformDropdown ? '1px solid rgba(0, 112, 243, 0.4)' : '1px solid rgba(15, 42, 74, 0.12)',
                      background: openFormPlatformDropdown ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.85)'
                    }}
                  >
                    <span>{PLAT_LABEL[form.platform] || form.platform}</span>
                    <span style={{
                      fontSize: '9px',
                      transition: 'transform 0.2s ease',
                      transform: openFormPlatformDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                      display: 'inline-block'
                    }}>
                      ▼
                    </span>
                  </button>

                  {openFormPlatformDropdown && (
                    <div style={{ ...glassDropdownMenu, left: 0, right: 0, width: '100%', boxSizing: 'border-box' }}>
                      {[
                        { id: 'BOTH', label: 'FB + Instagram' },
                        { id: 'FACEBOOK', label: 'Solo Facebook' },
                        { id: 'INSTAGRAM', label: 'Solo Instagram' }
                      ].map(pl => {
                        const isSelected = form.platform === pl.id;
                        return (
                          <div
                            key={pl.id}
                            onClick={() => {
                              setForm(f => ({ ...f, platform: pl.id }));
                              setOpenFormPlatformDropdown(false);
                            }}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              background: isSelected ? 'rgba(0, 112, 243, 0.08)' : 'transparent',
                              fontSize: '12px',
                              fontWeight: isSelected ? 800 : 600,
                              color: isSelected ? '#0070f3' : '#0f2a4a',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => {
                              if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'rgba(0, 112, 243, 0.05)';
                            }}
                            onMouseLeave={e => {
                              if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                            }}
                          >
                            <span>{pl.label}</span>
                            {isSelected && <IconCheck size={14} color="#0070f3" />}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {form.brandId && !hasMeta(form.brandId) && (
                <div style={{
                  background: 'rgba(254, 243, 199, 0.7)',
                  border: '1px solid rgba(245, 158, 11, 0.4)',
                  borderRadius: '12px',
                  padding: '10px 12px',
                  fontSize: '12px',
                  color: '#b45309',
                  fontWeight: 600,
                  boxShadow: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <IconAlertCircle size={15} color="#b45309" />
                  <span>Esta marca no tiene credenciales Meta vinculadas. El post se guardará como borrador.</span>
                </div>
              )}

              {/* Form Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: '12px',
                    border: '1px solid rgba(15, 42, 74, 0.12)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    color: '#0f2a4a',
                    boxShadow: 'none'
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  style={{
                    flex: 2,
                    padding: '11px',
                    borderRadius: '12px',
                    border: 'none',
                    background: '#0070f3',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    opacity: saving ? 0.7 : 1,
                    boxShadow: 'none',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    if (!saving) {
                      (e.currentTarget as HTMLElement).style.background = '#0060df';
                      (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0, 112, 243, 0.3)';
                    }
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = '#0070f3';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }}
                  onMouseDown={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'scale(0.98)';
                  }}
                  onMouseUp={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                  }}
                >
                  {saving ? 'Programando...' : 'Programar Publicación'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Conectar con Meta OAuth */}
      {showMetaModal && (
        <div style={glassModalOverlay} onClick={() => setShowMetaModal(false)}>
          <div
            style={{
              ...glassModalWindow,
              padding: '28px 32px',
              width: '450px',
              maxWidth: '92vw'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#0f2a4a', letterSpacing: '-0.2px' }}>
                  Conectar con Meta
                </div>
                <div style={{ fontSize: '12px', color: '#0070f3', fontWeight: 700 }}>
                  {selectedBrandObj?.name}
                </div>
              </div>
              <button
                onClick={() => setShowMetaModal(false)}
                style={{
                  background: 'rgba(15, 42, 74, 0.06)',
                  border: 'none',
                  width: '28px',
                  height: '28px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#0f2a4a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ✕
              </button>
            </div>

            {oauthPages.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '12.5px', color: 'rgba(15, 42, 74, 0.7)', marginBottom: '4px', fontWeight: 500 }}>
                  Selecciona la página de Facebook para <strong>{selectedBrandObj?.name}</strong>:
                </div>
                {oauthPages.map((p: any) => (
                  <div
                    key={p.id}
                    onClick={() => setSelectedPage(p)}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '12px',
                      border: `2px solid ${selectedPage?.id === p.id ? '#0070f3' : 'rgba(15, 42, 74, 0.08)'}`,
                      cursor: 'pointer',
                      background: selectedPage?.id === p.id ? 'rgba(0, 112, 243, 0.08)' : 'rgba(255, 255, 255, 0.7)',
                      transition: 'all 0.15s ease',
                      boxShadow: 'none'
                    }}
                  >
                    <div style={{ fontWeight: 800, fontSize: '13px', color: '#0f2a4a' }}>{p.name}</div>
                    <div style={{ fontSize: '11px', color: 'rgba(15, 42, 74, 0.45)', marginTop: '2px', fontWeight: 600 }}>
                      ID: {p.id} {p.instagramAccountId ? '· Instagram vinculado ✓' : '· Sin Instagram'}
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                  <button
                    onClick={() => setShowMetaModal(false)}
                    style={{
                      flex: 1,
                      padding: '11px',
                      borderRadius: '12px',
                      border: '1px solid rgba(15, 42, 74, 0.12)',
                      background: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif',
                      color: '#0f2a4a',
                      boxShadow: 'none'
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSaveOAuthPage}
                    disabled={!selectedPage || saving}
                    style={{
                      flex: 2,
                      padding: '11px',
                      borderRadius: '12px',
                      border: 'none',
                      background: '#0070f3',
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontFamily: '"Montserrat", sans-serif',
                      opacity: (!selectedPage || saving) ? 0.6 : 1,
                      boxShadow: 'none',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={e => {
                      if (selectedPage && !saving) {
                        (e.currentTarget as HTMLElement).style.background = '#0060df';
                        (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0, 112, 243, 0.3)';
                      }
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.background = '#0070f3';
                      (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                    }}
                    onMouseDown={e => {
                      (e.currentTarget as HTMLElement).style.transform = 'scale(0.98)';
                    }}
                    onMouseUp={e => {
                      (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                    }}
                  >
                    {saving ? 'Guardando...' : 'Conectar Página'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {hasMeta(metaForm.brandId) && (
                  <div style={{
                    background: 'rgba(240, 253, 244, 0.85)',
                    border: '1px solid rgba(16, 185, 129, 0.4)',
                    borderRadius: '12px',
                    padding: '10px 12px',
                    fontSize: '12px',
                    color: '#047857',
                    fontWeight: 600,
                    boxShadow: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <IconCheckCircle size={15} color="#047857" />
                    <span>Esta marca ya tiene Meta vinculado. Puedes reconectar si deseas actualizar permisos o páginas.</span>
                  </div>
                )}
                <div style={{
                  background: 'rgba(255, 255, 255, 0.65)',
                  border: '1px solid rgba(15, 42, 74, 0.08)',
                  borderRadius: '16px',
                  padding: '20px 16px',
                  textAlign: 'center',
                  boxShadow: 'none'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
                    <AppleEmoji symbol="🔗" size={36} />
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f2a4a', marginBottom: '4px' }}>
                    Autoriza el acceso a tus páginas
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(15, 42, 74, 0.55)', lineHeight: '1.5', fontWeight: 500 }}>
                    Se abrirá una ventana emergente segura de Facebook donde podrás elegir a qué páginas y cuentas de Instagram otorgar acceso.
                  </div>
                </div>

                <button
                  onClick={() => startMetaOAuth(metaForm.brandId)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '12px',
                    border: 'none',
                    background: '#1877f2',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: '"Montserrat", sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: 'none',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = '#1565c0';
                    (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(24, 119, 242, 0.3)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = '#1877f2';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }}
                  onMouseDown={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'scale(0.98)';
                  }}
                  onMouseUp={e => {
                    (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                  Continuar con Facebook
                </button>

                <div style={{ fontSize: '10.5px', color: 'rgba(15, 42, 74, 0.45)', textAlign: 'center', fontWeight: 500 }}>
                  Permisos: Gestión de páginas, publicaciones e Instagram Business
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
